import { useEffect, useState } from 'react'

/**
 * Chart colours read from CSS custom properties rather than hard-coded here.
 *
 * The palette is defined once in `styles.css` and validated against the surfaces it
 * actually renders on. Reading it back means the charts cannot drift from the rest of the
 * UI, and a theme change is one place to edit rather than two.
 */
export interface ChartTheme {
  /** Categorical slots 1..8, in fixed order. Assigned in sequence, never cycled. */
  series: string[]
  /** The folded tail and de-emphasis. Deliberately not a hue. */
  other: string
  text: string
  textMuted: string
  surface: string
  grid: string
  axis: string
  up: string
  down: string
  accent: string
}

function read(styles: CSSStyleDeclaration, name: string, fallback: string): string {
  const value = styles.getPropertyValue(name).trim()
  return value || fallback
}

function currentTheme(): ChartTheme {
  const styles = getComputedStyle(document.documentElement)
  return {
    series: [1, 2, 3, 4, 5, 6, 7, 8].map((slot) =>
      read(styles, `--series-${slot}`, '#3987e5'),
    ),
    other: read(styles, '--series-other', '#6a737d'),
    text: read(styles, '--text', '#e6edf3'),
    textMuted: read(styles, '--text-muted', '#8b949e'),
    surface: read(styles, '--bg-raised', '#161b22'),
    grid: read(styles, '--grid', '#2c2c2a'),
    axis: read(styles, '--axis', '#383835'),
    up: read(styles, '--up', '#0ca30c'),
    down: read(styles, '--down', '#d03b3b'),
    accent: read(styles, '--accent', '#3987e5'),
  }
}

/**
 * Dark mode is a selected set of steps, not an automatic flip of the light one, so the
 * charts have to be told when it changes rather than inverting anything themselves.
 */
export function useChartTheme(): ChartTheme {
  const [theme, setTheme] = useState<ChartTheme>(currentTheme)

  useEffect(() => {
    const update = () => setTheme(currentTheme())

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener('change', update)

    // The in-app toggle stamps `data-theme` on the root element, which changes the custom
    // properties without firing the media query. Canvas colours are read once and painted,
    // so without this the page turns dark around two charts that stay light.
    const observer = new MutationObserver(update)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    return () => {
      media.removeEventListener('change', update)
      observer.disconnect()
    }
  }, [])

  return theme
}

/**
 * The colour for one bucket, by its position in a fixed order.
 *
 * Position comes from the entity, never from its current rank, so filtering a bucket out
 * cannot repaint the ones that remain. Past the eighth slot the tail is folded into
 * "Other" rather than given a generated ninth hue, which would be indistinguishable from
 * an existing slot under colour-vision deficiency.
 */
export function seriesColor(theme: ChartTheme, index: number, key: string): string {
  if (key === OTHER_KEY || index >= theme.series.length) {
    return theme.other
  }
  return theme.series[index] ?? theme.other
}

export const OTHER_KEY = 'Other'

/** Slots available before the tail has to be folded. */
export const MAX_SERIES = 8
